import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, TrendingUp, ShoppingCart, Tag, Repeat, Award, Shield, 
  Lightbulb, ArrowRight, CheckCircle, DollarSign, Search, Eye,
  Star, BadgeCheck, MessageCircle, Camera, Users, Zap, Target,
  Coins, Scale, Trophy, Gamepad2, ChevronRight, AlertTriangle, ThumbsUp,
  BarChart3, Lock, UserCheck, FileCheck, Sparkles
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const VALUE_FACTORS = [
  { icon: Award, title: 'Condition', description: 'Better condition = higher value. Even small scratches can affect price significantly.' },
  { icon: Star, title: 'Rarity', description: 'Limited mintage, errors, or historical significance make items more valuable.' },
  { icon: TrendingUp, title: 'Demand', description: 'Popular series, trending collectibles, and investor interest drive prices up.' },
  { icon: Scale, title: 'Metal Content', description: 'For precious metals, spot price + premium determines base value.' },
];

const BEGINNER_TIPS = [
  { icon: Target, tip: 'Start Small', description: 'Begin with affordable items to learn the market before making big purchases.' },
  { icon: BookOpen, tip: 'Research First', description: 'Learn about what you\'re buying. Knowledge protects you from bad deals.' },
  { icon: BadgeCheck, tip: 'Buy from Trusted Sellers', description: 'Check ratings, reviews, and verification badges before purchasing.' },
  { icon: MessageCircle, tip: 'Ask Questions', description: 'Use messaging to ask sellers about condition, authenticity, and history.' },
  { icon: Camera, tip: 'Request Photos', description: 'Ask for additional images if listing photos aren\'t clear enough.' },
  { icon: FileCheck, tip: 'Keep Records', description: 'Save receipts, certificates, and transaction details for your collection.' },
];

const TRUST_FEATURES = [
  { icon: UserCheck, title: 'Verified Users', description: 'Sellers can verify their identity for added trust and credibility.' },
  { icon: Star, title: 'Ratings & Reviews', description: 'See what other buyers say before you buy. Leave feedback after transactions.' },
  { icon: Lock, title: 'Secure Transactions', description: 'Your payments and personal information are protected.' },
  { icon: Shield, title: 'Buyer Protection', description: 'Dispute resolution and support if something goes wrong.' },
];

export default function LearnPage() {
  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-8">
              <BookOpen className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold font-medium">Beginner's Guide</span>
            </div>
            
            <h1 className="font-clash text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
              New to Coins & Collectibles?{' '}
              <span className="gold-text">Start Here.</span>
            </h1>
            
            <p className="text-xl text-zinc-400 mb-10 leading-relaxed">
              Learn how to buy, sell, trade, and build value with real assets. 
              We'll guide you through everything you need to know.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/marketplace"
                className="btn-gold px-8 py-4 flex items-center justify-center gap-2"
                data-testid="learn-start-buying"
              >
                <ShoppingCart className="w-5 h-5" /> Start Buying
              </Link>
              <Link
                to="/create-listing"
                className="btn-secondary px-8 py-4 flex items-center justify-center gap-2"
                data-testid="learn-start-selling"
              >
                <Tag className="w-5 h-5" /> Start Selling
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What Is This Market? */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">
                Understanding The Market
              </p>
              <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-6">
                What Are Real Assets?
              </h2>
              <p className="text-lg text-zinc-400 mb-6 leading-relaxed">
                Coins, precious metals, and collectibles are <span className="text-white font-medium">tangible assets</span> — 
                physical items with real, measurable value. Unlike stocks or crypto, you can hold them in your hand.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 p-4 bg-surface-base border border-border-subtle">
                  <Coins className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-white mb-1">Coins & Currency</h4>
                    <p className="text-sm text-zinc-400">Rare coins, vintage currency, and numismatic collectibles with historical significance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface-base border border-border-subtle">
                  <Scale className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-white mb-1">Precious Metals</h4>
                    <p className="text-sm text-zinc-400">Gold, silver, platinum bars and coins — a hedge against inflation and economic uncertainty.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface-base border border-border-subtle">
                  <Trophy className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-white mb-1">Sports Cards</h4>
                    <p className="text-sm text-zinc-400">Baseball, basketball, football cards — graded and raw collectibles from every era.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface-base border border-border-subtle">
                  <Gamepad2 className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-white mb-1">TCG / Gaming Cards</h4>
                    <p className="text-sm text-zinc-400">Pokemon, Magic: The Gathering, Yu-Gi-Oh and other trading card games.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-surface-base border border-border-subtle">
                  <Sparkles className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-white mb-1">Collectibles</h4>
                    <p className="text-sm text-zinc-400">Marvel, Star Wars, Disney, pop culture memorabilia and other valuable collector items.</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gold/5 border border-gold/20">
                <p className="text-sm text-zinc-300">
                  <span className="text-gold font-semibold">Why do people invest?</span> Real assets can appreciate over time, 
                  provide portfolio diversification, and offer the joy of collecting something meaningful.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square bg-surface-elevated border border-border-subtle overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1610375461246-83df859d849d?w=600"
                  alt="Gold bars and coins"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gold text-black p-6">
                <p className="font-clash text-3xl font-bold">$12T+</p>
                <p className="text-sm">Global Collectibles Market</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Buy */}
      <section className="py-20 bg-surface-base" id="how-to-buy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-emerald-500 font-semibold">
                Step-by-Step Guide
              </p>
            </div>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4">
              How to Buy on CoinHustle
            </h2>
            <p className="text-lg text-zinc-400">
              Finding and purchasing your first asset is easier than you think.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { step: '01', title: 'Browse Listings', description: 'Search by category, price, condition, or use filters to find exactly what you want.', icon: Search },
              { step: '02', title: 'Research the Item', description: 'Check seller ratings, read descriptions, and ask questions before buying.', icon: Eye },
              { step: '03', title: 'Choose How to Buy', description: 'Buy Now for instant purchase, Make Offer to negotiate, or bid on Auctions.', icon: DollarSign },
              { step: '04', title: 'Complete Purchase', description: 'Secure checkout, seller ships your item, leave a review when it arrives.', icon: CheckCircle },
            ].map((item) => (
              <div key={item.step} className="p-6 bg-surface-card border border-border-subtle relative group hover:border-emerald-500/30 transition-colors">
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-emerald-500 text-black font-clash font-bold flex items-center justify-center text-sm">
                  {item.step}
                </div>
                <item.icon className="w-8 h-8 text-emerald-500 mb-4 mt-2" />
                <h3 className="font-clash text-lg font-medium text-white mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-400">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="p-6 bg-surface-card border border-border-subtle">
            <h4 className="font-clash text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-gold" /> Tips for First-Time Buyers
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Compare prices across multiple listings before buying',
                'Understand "spot price" vs "premium" for precious metals',
                'Check if shipping and insurance are included',
                'Read return policies before making a purchase',
              ].map((tip) => (
                <div key={tip} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-zinc-300">{tip}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/marketplace" className="btn-gold px-8 py-4 inline-flex items-center gap-2">
              Browse Marketplace <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How to Sell */}
      <section className="py-20 bg-surface-card" id="how-to-sell">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square bg-surface-elevated border border-border-subtle overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1612734425688-d7a59377e5f7?w=400"
                    alt="Gold coins"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-surface-elevated border border-border-subtle overflow-hidden mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1633869699811-cd4f63049b36?w=400"
                    alt="Collectibles"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold">
                  Sell Your Items
                </p>
              </div>
              <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-6">
                How to Sell on CoinHustle
              </h2>
              <p className="text-lg text-zinc-400 mb-8">
                Turn your collection into cash in just a few steps.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { num: '1', title: 'Create a Listing', desc: 'Add title, description, category, and condition.' },
                  { num: '2', title: 'Upload Quality Photos', desc: 'Clear, well-lit images from multiple angles.' },
                  { num: '3', title: 'Set Your Price', desc: 'Fixed price, auction, or accept offers.' },
                  { num: '4', title: 'Enable Trading', desc: 'Allow buyers to propose trades (optional).' },
                  { num: '5', title: 'Connect & Sell', desc: 'Respond to buyers and close the deal.' },
                ].map((step) => (
                  <div key={step.num} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-blue-500/10 text-blue-500 font-clash font-bold flex items-center justify-center flex-shrink-0">
                      {step.num}
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{step.title}</h4>
                      <p className="text-sm text-zinc-400">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-blue-500/5 border border-blue-500/20 mb-8">
                <p className="text-sm text-zinc-300">
                  <span className="text-blue-400 font-semibold">Pro Tip:</span> Listings with 5+ quality photos 
                  and detailed descriptions sell 3x faster on average.
                </p>
              </div>

              <Link to="/create-listing" className="btn-gold px-8 py-4 inline-flex items-center gap-2">
                Start Selling <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How to Trade */}
      <section className="py-20 bg-surface-base" id="how-to-trade">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
              <Repeat className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold font-medium">Our Key Feature</span>
            </div>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4">
              How to Trade on CoinHustle
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Trading is what makes CoinHustle different. Swap items directly with other collectors — 
              no cash needed (or combine items + cash).
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="p-8 bg-surface-card border border-gold/20 relative">
              <div className="absolute -top-4 left-8 px-4 py-1 bg-gold text-black font-semibold text-sm">
                TRADE TYPE 1
              </div>
              <Repeat className="w-10 h-10 text-gold mb-4 mt-2" />
              <h3 className="font-clash text-xl font-medium text-white mb-3">Item-for-Item</h3>
              <p className="text-zinc-400 mb-4">
                Swap your item directly for another. Perfect when you find something you want and have something to offer.
              </p>
              <div className="p-3 bg-surface-base text-sm text-zinc-300">
                Example: Trade your 1964 Kennedy Half Dollar for a 1921 Morgan Dollar
              </div>
            </div>

            <div className="p-8 bg-surface-card border border-gold/20 relative">
              <div className="absolute -top-4 left-8 px-4 py-1 bg-gold text-black font-semibold text-sm">
                TRADE TYPE 2
              </div>
              <DollarSign className="w-10 h-10 text-gold mb-4 mt-2" />
              <h3 className="font-clash text-xl font-medium text-white mb-3">Item + Cash</h3>
              <p className="text-zinc-400 mb-4">
                When values don't match perfectly, add cash to balance the deal. Flexible and fair.
              </p>
              <div className="p-3 bg-surface-base text-sm text-zinc-300">
                Example: Your coin ($500) + $200 cash for their coin ($700)
              </div>
            </div>

            <div className="p-8 bg-surface-card border border-gold/20 relative">
              <div className="absolute -top-4 left-8 px-4 py-1 bg-gold text-black font-semibold text-sm">
                TRADE TYPE 3
              </div>
              <Users className="w-10 h-10 text-gold mb-4 mt-2" />
              <h3 className="font-clash text-xl font-medium text-white mb-3">Multiple Items</h3>
              <p className="text-zinc-400 mb-4">
                Offer multiple items from your collection in a single trade proposal.
              </p>
              <div className="p-3 bg-surface-base text-sm text-zinc-300">
                Example: Trade 3 silver coins for 1 gold coin
              </div>
            </div>
          </div>

          <div className="p-6 bg-surface-card border border-border-subtle mb-8">
            <h4 className="font-clash text-lg font-medium text-white mb-4">How Trading Works:</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { num: '1', text: 'Find a trade-enabled listing' },
                { num: '2', text: 'Click "Propose a Trade"' },
                { num: '3', text: 'Select items from your listings + optional cash' },
                { num: '4', text: 'Seller accepts, rejects, or counters' },
              ].map((step) => (
                <div key={step.num} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gold text-black font-clash font-bold flex items-center justify-center flex-shrink-0">
                    {step.num}
                  </div>
                  <p className="text-sm text-zinc-300">{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Link to="/marketplace?trade=true" className="btn-gold px-8 py-4 inline-flex items-center gap-2">
              Explore Trade-Enabled Items <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Understanding Value */}
      <section className="py-20 bg-surface-card" id="understanding-value">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-purple-500 font-semibold">
                Pricing Knowledge
              </p>
            </div>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4">
              Understanding Value
            </h2>
            <p className="text-lg text-zinc-400">
              Know what affects the price of coins, metals, and collectibles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {VALUE_FACTORS.map((factor) => (
              <div key={factor.title} className="p-6 bg-surface-base border border-border-subtle hover:border-purple-500/30 transition-colors">
                <factor.icon className="w-8 h-8 text-purple-500 mb-4" />
                <h3 className="font-clash text-lg font-medium text-white mb-2">{factor.title}</h3>
                <p className="text-sm text-zinc-400">{factor.description}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="p-6 bg-surface-base border border-border-subtle">
              <h4 className="font-clash text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-gold" /> What is Grading?
              </h4>
              <p className="text-zinc-400 mb-4">
                Professional grading services (like PCGS, NGC) evaluate and certify the condition of coins 
                on a scale from 1-70. Higher grades = better condition = higher value.
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">MS-70 (Perfect)</span>
                  <span className="text-gold">Highest Value</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">MS-65 (Gem)</span>
                  <span className="text-zinc-300">Premium</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">MS-60 (Uncirculated)</span>
                  <span className="text-zinc-400">Above Average</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">VF-30 (Very Fine)</span>
                  <span className="text-zinc-500">Circulated</span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-surface-base border border-border-subtle">
              <h4 className="font-clash text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-gold" /> Precious Metals Pricing
              </h4>
              <p className="text-zinc-400 mb-4">
                Metal prices are based on "spot price" (current market value) plus a "premium" 
                (dealer markup for minting, distribution, and profit).
              </p>
              <div className="p-4 bg-surface-card border border-border-subtle">
                <p className="text-sm text-zinc-300 mb-2">
                  <span className="font-medium">Example:</span> 1oz Gold Eagle
                </p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Spot Price:</span>
                  <span className="text-white">$2,000</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Premium (5%):</span>
                  <span className="text-white">$100</span>
                </div>
                <div className="flex justify-between text-sm border-t border-border-subtle pt-2 mt-2">
                  <span className="text-zinc-300 font-medium">Total Price:</span>
                  <span className="text-gold font-medium">$2,100</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-20 bg-surface-base" id="trust-safety">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-emerald-500 font-medium">Your Security Matters</span>
            </div>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4">
              Trust & Safety
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              CoinHustle is built on trust. Here's how we keep you safe.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {TRUST_FEATURES.map((feature) => (
              <div key={feature.title} className="p-6 bg-surface-card border border-border-subtle text-center hover:border-emerald-500/30 transition-colors">
                <div className="w-14 h-14 bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-emerald-500" />
                </div>
                <h3 className="font-clash text-lg font-medium text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="p-6 bg-surface-card border border-red-500/20">
            <h4 className="font-clash text-lg font-medium text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Tips to Avoid Scams
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Never pay outside the platform',
                'Be wary of deals that seem too good to be true',
                'Check seller history and reviews',
                'Ask for additional photos if unsure',
                'Report suspicious activity immediately',
                'Trust your instincts — if something feels off, walk away',
              ].map((tip) => (
                <div key={tip} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-zinc-300">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beginner Tips */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
              <Lightbulb className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold font-medium">Expert Advice</span>
            </div>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4">
              Beginner Tips
            </h2>
            <p className="text-lg text-zinc-400">
              Advice from experienced collectors to help you succeed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BEGINNER_TIPS.map((tip) => (
              <div key={tip.tip} className="p-6 bg-surface-base border border-border-subtle hover:border-gold/30 transition-colors group">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                  <tip.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-clash text-lg font-medium text-white mb-2">{tip.tip}</h3>
                <p className="text-sm text-zinc-400">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">
              Choose Your Path
            </p>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4">
              What Do You Want to Do?
            </h2>
            <p className="text-lg text-zinc-400">
              Select the path that fits your goals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Buy Path */}
            <div className="p-8 bg-gradient-to-b from-emerald-500/10 to-surface-card border border-emerald-500/20 hover:border-emerald-500/40 transition-colors group">
              <div className="w-16 h-16 bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:bg-emerald-500/20 transition-colors">
                <ShoppingCart className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="font-clash text-2xl font-medium text-white mb-4">
                "I want to buy assets"
              </h3>
              <p className="text-zinc-400 mb-6">
                Start your collection or add to your investment portfolio. Browse thousands of listings from trusted sellers.
              </p>
              <ul className="space-y-3 mb-8">
                {['Browse verified listings', 'Compare prices easily', 'Buy, bid, or make offers', 'Buyer protection included'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-emerald-500" /> {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/marketplace" 
                className="w-full py-4 bg-emerald-500 text-black font-semibold flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors"
              >
                Browse Marketplace <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Sell Path */}
            <div className="p-8 bg-gradient-to-b from-blue-500/10 to-surface-card border border-blue-500/20 hover:border-blue-500/40 transition-colors group">
              <div className="w-16 h-16 bg-blue-500/10 flex items-center justify-center mb-6 group-hover:bg-blue-500/20 transition-colors">
                <Tag className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="font-clash text-2xl font-medium text-white mb-4">
                "I want to sell my collection"
              </h3>
              <p className="text-zinc-400 mb-6">
                Turn your collection into cash. List your items and reach thousands of serious buyers.
              </p>
              <ul className="space-y-3 mb-8">
                {['Free listings to start', 'Set your own prices', 'Accept offers or trades', 'Connect with real buyers'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-blue-500" /> {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/create-listing" 
                className="w-full py-4 bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 hover:bg-blue-400 transition-colors"
              >
                Start Selling <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Trade Path */}
            <div className="p-8 bg-gradient-to-b from-gold/10 to-surface-card border border-gold/20 hover:border-gold/40 transition-colors group">
              <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                <Repeat className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-clash text-2xl font-medium text-white mb-4">
                "I want to trade and flip"
              </h3>
              <p className="text-zinc-400 mb-6">
                Swap items, flip for profit, and grow your collection through smart trades.
              </p>
              <ul className="space-y-3 mb-8">
                {['Propose item-for-item trades', 'Combine items + cash', 'Negotiate directly', 'Build without spending cash'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-gold" /> {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/marketplace?trade=true" 
                className="w-full py-4 bg-gold text-black font-semibold flex items-center justify-center gap-2 hover:bg-gold-hover transition-colors"
              >
                Explore Trading <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-b from-surface-card to-surface-base relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(212,175,55,0.1) 10px, rgba(212,175,55,0.1) 20px)'
          }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Zap className="w-12 h-12 text-gold mx-auto mb-6" />
          <h2 className="font-clash text-4xl sm:text-5xl font-medium text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Join thousands of collectors and investors building wealth with real assets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn-gold px-10 py-4 text-lg flex items-center justify-center gap-2"
              data-testid="learn-join-cta"
            >
              Join CoinHustle Exchange <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/marketplace"
              className="btn-secondary px-10 py-4 text-lg flex items-center justify-center gap-2"
            >
              Browse Marketplace
            </Link>
          </div>
          <p className="text-sm text-zinc-500 mt-8">
            Free to join. No listing fees on your first 5 items.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
